import { errorMessage } from '@runnerboy/api-client';
import type { OrderStatus } from '@runnerboy/types';
import { Alert, Badge, Button, Card, cx, EmptyState, Field, humanize, Input, inr, Select, Sheet, Skeleton, STATUS_TONE, Tabs, timeAgo, useToast, volume } from '@runnerboy/ui';
import { BellRing, Bike, CheckCircle2, Clock, PackageCheck, QrCode, ShieldAlert, Undo2 } from 'lucide-react';
import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { alarm, client, useStoreSession } from './session';

interface QueueOrder {
  id: string;
  code: string;
  status: OrderStatus;
  prepStatus: string;
  placedAt: string | null;
  acceptedAt: string | null;
  prepMinutes: number | null;
  estimatedReadyAt: string | null;
  totalMinor: number;
  itemCount: number;
  packageCount: number;
  containsAlcohol: boolean;
  complianceHold: boolean;
  pickupCode: string;
  sealCode: string | null;
  locality: string | null;
  hotel: string | null;
  items: { id: string; name: string; brand: string; volumeMl: number | null; quantity: number; unitPrice: number; productType: string }[];
  runner: { id: string; name: string; rating: number | null; vehicle: string | null; checkedInAt: string | null; scannedAt: string | null } | null;
  acceptDeadline: string | null;
}
interface Queue {
  active: QueueOrder[];
  recent: { id: string; code: string; status: OrderStatus; totalMinor: number; itemCount: number; placedAt: string }[];
}

type Tab = 'new' | 'preparing' | 'ready' | 'returns' | 'done';
const tabOf = (o: QueueOrder): Tab =>
  o.status === 'STORE_CONFIRMATION_PENDING' ? 'new' : ['DELIVERY_REFUSED', 'RETURN_TO_STORE'].includes(o.status) ? 'returns' : o.prepStatus === 'SEALED' || o.status === 'READY_FOR_PICKUP' ? 'ready' : 'preparing';

export function OrdersPage() {
  const { t } = useTranslation();
  const { store, socket } = useStoreSession();
  const [queue, setQueue] = useState<Queue | null>(null);
  const [tab, setTab] = useState<Tab>('new');
  const [soundOn, setSoundOn] = useState(alarm.unlocked);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    if (!store) return;
    try {
      setQueue(await client.get<Queue>(`/store/stores/${store.id}/orders`));
    } catch {
      /* keep last good state; banner shows connection */
    }
  }, [store]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 10_000);
    const tick = setInterval(() => setTick((x) => x + 1), 1000);
    return () => {
      clearInterval(id);
      clearInterval(tick);
    };
  }, [load]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => void load();
    const onNew = (p: { code: string }) => {
      void load();
      if (Notification?.permission === 'granted') new Notification('New Runner Boy order', { body: `Order ${p.code}`, tag: p.code, requireInteraction: true });
    };
    socket.on('store.order.new', onNew);
    socket.on('store.order.updated', refresh);
    socket.on('store.runner.scanned', refresh);
    socket.on('order.status', refresh);
    return () => {
      socket.off('store.order.new', onNew);
      socket.off('store.order.updated', refresh);
      socket.off('store.runner.scanned', refresh);
      socket.off('order.status', refresh);
    };
  }, [socket, load]);

  const pending = queue?.active.filter((o) => o.status === 'STORE_CONFIRMATION_PENDING').length ?? 0;
  useEffect(() => {
    if (pending > 0 && soundOn) alarm.start();
    else alarm.stop();
    return () => alarm.stop();
  }, [pending, soundOn]);

  const counts = (k: Tab) => queue?.active.filter((o) => tabOf(o) === k).length ?? 0;
  const list = tab === 'done' ? [] : (queue?.active ?? []).filter((o) => tabOf(o) === tab);

  return (
    <div>
      {!soundOn && (
        <Alert tone="warning" className="mb-4" title={t('store.soundTitle', 'Turn on order alerts')}>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={async () => {
                alarm.unlock();
                if ('Notification' in window && Notification.permission === 'default') await Notification.requestPermission();
                setSoundOn(true);
              }}
            >
              <BellRing className="h-4 w-4" /> {t('store.enableSound', 'Enable sound & notifications')}
            </Button>
          </div>
        </Alert>
      )}
      {pending > 0 && (
        <div role="alert" className="mb-4 flex animate-pulse items-center gap-3 rounded-[16px] bg-marigold-500 px-4 py-3 font-bold text-ink-950">
          <BellRing className="h-6 w-6" /> {t('store.newOrders', '{{count}} new order(s) waiting for you', { count: pending })}
        </div>
      )}
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'new', label: t('store.tabNew', 'New'), count: counts('new') },
          { value: 'preparing', label: t('store.tabPreparing', 'Preparing'), count: counts('preparing') },
          { value: 'ready', label: t('store.tabReady', 'Ready / pickup'), count: counts('ready') },
          { value: 'returns', label: t('store.tabReturns', 'Returns'), count: counts('returns') },
          { value: 'done', label: t('store.tabDone', 'Last 24h') },
        ]}
      />
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {!queue && [0, 1].map((i) => <Skeleton key={i} className="h-64" />)}
        {queue && tab !== 'done' && list.length === 0 && (
          <Card className="lg:col-span-2">
            <EmptyState icon={<PackageCheck className="h-8 w-8" />} title={t('store.noOrders', 'Nothing here right now')} body={t('store.noOrdersBody', 'New orders appear instantly with a sound alert.')} />
          </Card>
        )}
        {list.map((o) => (
          <OrderCard key={o.id} order={o} onChange={() => void load()} />
        ))}
        {tab === 'done' && (
          <Card className="divide-y divide-ink-100 lg:col-span-2">
            {(queue?.recent ?? []).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4">
                <div>
                  <div className="font-bold">{r.code}</div>
                  <div className="text-xs text-ink-500">
                    {timeAgo(r.placedAt)} · {r.itemCount} items · {inr(r.totalMinor)}
                  </div>
                </div>
                <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>{humanize(r.status)}</Badge>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order: o, onChange }: { order: QueueOrder; onChange: () => void }) {
  const { t } = useTranslation();
  const { store } = useStoreSession();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [prep, setPrep] = useState(String(store?.avgPrepMinutes ?? 10));
  const [rejectOpen, setRejectOpen] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const base = `/store/stores/${store?.id}/orders/${o.id}`;
  const act = async (key: string, path: string, body: unknown = {}) => {
    setBusy(key);
    try {
      await client.post(`${base}/${path}`, body);
      onChange();
      return true;
    } catch (e) {
      toast('danger', errorMessage(e));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const secondsLeft = o.acceptDeadline ? Math.max(0, Math.round((Date.parse(o.acceptDeadline) - Date.now()) / 1000)) : null;

  return (
    <Card as="article" className={cx('flex flex-col overflow-hidden', o.status === 'STORE_CONFIRMATION_PENDING' && 'ring-2 ring-marigold-500')} aria-label={`Order ${o.code}`}>
      <div className="flex items-start justify-between gap-3 border-b border-ink-100 p-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-extrabold tracking-tight">{o.code}</h3>
            {o.containsAlcohol && <Badge tone="dark">21+</Badge>}
          </div>
          <div className="mt-0.5 text-xs text-ink-500">
            {timeAgo(o.placedAt)} · {o.locality}
            {o.hotel && ` · ${o.hotel}`}
          </div>
        </div>
        <div className="text-right">
          <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{humanize(o.status)}</Badge>
          {secondsLeft !== null && (
            <div className={cx('rb-tabular mt-1 flex items-center justify-end gap-1 text-sm font-bold', secondsLeft < 60 ? 'text-danger-600' : 'text-ink-700')}>
              <Clock className="h-4 w-4" /> {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </div>
          )}
        </div>
      </div>
      {o.complianceHold && (
        <Alert tone="danger" className="m-4 mb-0" title={t('store.hold', 'Compliance hold — do not hand over')}>
          {t('store.holdBody', 'Operations will contact you.')}
        </Alert>
      )}
      <ul className="flex-1 divide-y divide-ink-50 px-4">
        {o.items.map((i) => (
          <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <span>
              <span className="rb-tabular mr-2 inline-grid h-6 min-w-6 place-items-center rounded-md bg-ink-900 px-1.5 text-xs font-bold text-white">{i.quantity}</span>
              {i.name} <span className="text-ink-500">{volume(i.volumeMl)}</span>
            </span>
            <span className="rb-tabular text-ink-700">{inr(i.unitPrice * i.quantity)}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between px-4 py-2 text-sm">
        <span className="text-ink-500">{t('store.total', 'Total (items)')}</span>
        <span className="rb-tabular font-bold">{inr(o.items.reduce((s, i) => s + i.unitPrice * i.quantity, 0))}</span>
      </div>

      {o.runner && (
        <div className="mx-4 mb-3 flex items-center gap-3 rounded-[14px] bg-peacock-50 p-3">
          <Bike className="h-5 w-5 text-peacock-700" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-bold">{o.runner.name}</div>
            <div className="text-xs text-ink-600">
              {o.runner.vehicle} · {o.runner.checkedInAt ? t('store.runnerHere', 'At store') : t('store.runnerOnWay', 'On the way')}
              {o.runner.scannedAt && ` · ${t('store.scanned', 'Scanned order code')}`}
            </div>
          </div>
          {o.runner.scannedAt && <CheckCircle2 className="h-5 w-5 text-success-600" />}
        </div>
      )}

      <div className="flex flex-col gap-2.5 border-t border-ink-100 bg-ink-50/60 p-4">
        {o.status === 'STORE_CONFIRMATION_PENDING' && (
          <>
            {/* Busy counter: one tap sets prep time, one tap accepts. */}
            <div className="flex items-center gap-2">
              <span className="rb-eyebrow shrink-0 text-[10px] text-ink-500">{t('store.prepTime', 'Prep')}</span>
              <div className="flex flex-1 gap-1.5" role="radiogroup" aria-label={t('store.prepTime', 'Preparation time')}>
                {['5', '10', '15', '20', '30'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={prep === m}
                    onClick={() => setPrep(m)}
                    className={cx('rb-press rb-num h-10 flex-1 rounded-full text-[13px] font-semibold', prep === m ? 'bg-ink-900 text-white' : 'bg-white text-ink-700 ring-1 ring-ink-900/[0.1]')}
                  >
                    {m}m
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="lg" variant="accent" className="flex-1" loading={busy === 'accept'} onClick={() => void act('accept', 'accept', { prepMinutes: Number(prep) })}>
                <CheckCircle2 className="h-5 w-5" /> {t('store.acceptIn', 'Accept · ready in {{m}} min', { m: prep })}
              </Button>
              <Button size="lg" variant="secondary" onClick={() => setRejectOpen(true)}>
                {t('store.reject', 'Reject')}
              </Button>
            </div>
          </>
        )}
        {tabOf(o) === 'preparing' && (
          <div className="flex gap-2">
            {o.prepStatus === 'NOT_STARTED' && (
              <Button size="lg" variant="secondary" loading={busy === 'prep'} onClick={() => void act('prep', 'preparing')}>
                {t('store.startPacking', 'Start packing')}
              </Button>
            )}
            <Button size="lg" className="flex-1" disabled={o.complianceHold} onClick={() => setSealOpen(true)}>
              <PackageCheck className="h-5 w-5" /> {t('store.sealed', 'Packed & sealed')}
            </Button>
          </div>
        )}
        {tabOf(o) === 'ready' && (
          <>
            <div className="flex gap-2">
              <PickupCode code={o.pickupCode} seal={o.sealCode} />
              <Button size="lg" variant={o.runner?.scannedAt ? 'accent' : 'primary'} className="flex-1 whitespace-nowrap" loading={busy === 'handover'} disabled={!o.runner?.scannedAt || o.complianceHold} onClick={() => void act('handover', 'handover')}>
                {t('store.handoverShort', 'Hand over to runner')}
              </Button>
            </div>
            {!o.runner?.scannedAt && <p className="text-xs text-ink-500">{t('store.handoverHint', 'The runner must scan the order code and confirm the seal before you hand over.')}</p>}
          </>
        )}
        {tabOf(o) === 'returns' && (
          <Button size="lg" className="w-full" disabled={o.status !== 'RETURN_TO_STORE'} onClick={() => setReturnOpen(true)}>
            <Undo2 className="h-5 w-5" /> {t('store.receiveReturn', 'Receive returned order')}
          </Button>
        )}
        <button type="button" onClick={() => setIssueOpen(true)} className="rb-press inline-flex items-center gap-1.5 self-end rounded-full px-2 py-1 text-[12px] font-semibold text-ink-500 hover:bg-ink-900/[0.05]">
          <ShieldAlert className="h-3.5 w-3.5" /> {t('store.reportIssue', 'Report issue')}
        </button>
      </div>

      <RejectSheet open={rejectOpen} onClose={() => setRejectOpen(false)} onReject={(reason, note) => act('reject', 'reject', { reason, note }).then((ok) => ok && setRejectOpen(false))} />
      <SealSheet open={sealOpen} onClose={() => setSealOpen(false)} onSeal={(sealCode, packageCount) => act('seal', 'ready', { sealCode: sealCode || undefined, packageCount }).then((ok) => ok && setSealOpen(false))} />
      <Sheet open={returnOpen} onClose={() => setReturnOpen(false)} title={t('store.receiveReturn', 'Receive returned order')}>
        <p className="text-sm text-ink-600">{t('store.returnBody', 'Check the bag. Is the original seal intact?')}</p>
        <p className="mt-1 text-sm font-semibold">Seal: {o.sealCode}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button onClick={() => void act('return', 'return-received', { sealIntact: true }).then((ok) => ok && setReturnOpen(false))}>{t('store.sealIntact', 'Seal intact — restock')}</Button>
          <Button variant="danger" onClick={() => void act('return', 'return-received', { sealIntact: false, notes: 'Seal broken on return' }).then((ok) => ok && setReturnOpen(false))}>
            {t('store.sealBroken', 'Seal broken — damaged')}
          </Button>
        </div>
      </Sheet>
      <IssueSheet open={issueOpen} onClose={() => setIssueOpen(false)} onSubmit={(kind, note) => act('issue', 'issue', { kind, note }).then((ok) => ok && setIssueOpen(false))} />
    </Card>
  );
}

function PickupCode({ code, seal }: { code: string; seal: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(`RBPICKUP:${code}`, { margin: 1, width: 280, color: { dark: '#0E1324', light: '#ffffff' } }).then(setUrl);
  }, [code]);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <QrCode className="h-4 w-4" /> {code}
      </Button>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('store.pickupCode', 'Pickup code')}>
        <div className="flex flex-col items-center gap-3">
          {url && <img src={url} alt={`QR code for pickup code ${code}`} className="h-64 w-64" />}
          <div className="rb-tabular text-3xl font-extrabold tracking-[0.3em]">{code}</div>
          {seal && <Badge tone="brand">Seal {seal}</Badge>}
          <p className="text-center text-sm text-ink-500">{t('store.pickupBody', 'Show this to the assigned runner. They scan it and confirm the seal and package count.')}</p>
        </div>
      </Sheet>
    </>
  );
}

const REJECT = ['OUT_OF_STOCK', 'STORE_CLOSING', 'CANNOT_FULFIL', 'PRICE_MISMATCH', 'COMPLIANCE_CONCERN', 'OTHER'] as const;
function RejectSheet({ open, onClose, onReject }: { open: boolean; onClose: () => void; onReject: (reason: string, note?: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string>('OUT_OF_STOCK');
  const [note, setNote] = useState('');
  return (
    <Sheet open={open} onClose={onClose} title={t('store.rejectTitle', 'Reject order')}>
      <Field label={t('store.reason', 'Reason')} htmlFor="reason">
        <Select id="reason" value={reason} onChange={(e) => setReason(e.target.value)}>
          {REJECT.map((r) => (
            <option key={r} value={r}>
              {humanize(r)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="mt-3">
        <Field label={t('store.note', 'Note (optional)')} htmlFor="note">
          <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <Alert tone="info" className="mt-3">{t('store.rejectInfo', 'Stock is released and the customer is refunded in full automatically.')}</Alert>
      <Button variant="danger" block size="lg" className="mt-4" onClick={() => onReject(reason, note || undefined)}>
        {t('store.reject', 'Reject')}
      </Button>
    </Sheet>
  );
}

function SealSheet({ open, onClose, onSeal }: { open: boolean; onClose: () => void; onSeal: (seal: string, packages: number) => void }) {
  const { t } = useTranslation();
  const [seal, setSeal] = useState('');
  const [packages, setPackages] = useState(1);
  return (
    <Sheet open={open} onClose={onClose} title={t('store.sealTitle', 'Pack & seal')}>
      <p className="text-sm text-ink-600">{t('store.sealBody', 'Seal the bag with a tamper-evident label and enter its code. Leave blank to generate one.')}</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Field label={t('store.sealCode', 'Seal / label code')} htmlFor="seal">
          <Input id="seal" value={seal} onChange={(e) => setSeal(e.target.value.toUpperCase())} placeholder="SEAL-…" />
        </Field>
        <Field label={t('store.packages', 'Packages')} htmlFor="pk">
          <Input id="pk" type="number" min={1} max={10} value={packages} onChange={(e) => setPackages(Math.max(1, Number(e.target.value)))} />
        </Field>
      </div>
      <Button block size="lg" className="mt-4" onClick={() => onSeal(seal, packages)}>
        {t('store.markReady', 'Mark ready for pickup')}
      </Button>
    </Sheet>
  );
}

function IssueSheet({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (kind: string, note: string) => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState('MISSING_RUNNER');
  const [note, setNote] = useState('');
  return (
    <Sheet open={open} onClose={onClose} title={t('store.reportIssue', 'Report issue')}>
      <Field label={t('store.issueKind', 'Issue')} htmlFor="kind">
        <Select id="kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="MISSING_RUNNER">{t('store.missingRunner', 'Runner has not arrived')}</option>
          <option value="STOCK_ISSUE">{t('store.stockIssue', 'Stock issue')}</option>
          <option value="OTHER">{t('store.other', 'Other')}</option>
        </Select>
      </Field>
      <div className="mt-3">
        <Field label={t('store.details', 'Details')} htmlFor="details">
          <Input id="details" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <Button block size="lg" className="mt-4" disabled={note.length < 2} onClick={() => onSubmit(kind, note)}>
        {t('store.send', 'Send to operations')}
      </Button>
    </Sheet>
  );
}
